import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserCheck, CheckCircle2, XCircle, Loader2, Tag, Sparkles, AlertCircle } from "lucide-react";

export default function StepAgentReferral({ formData, setFormData, errors, onValidateReferral, onValidationStateChange }) {
  const [hasReferralCode, setHasReferralCode] = useState(
    formData.hasReferralCode !== undefined ? formData.hasReferralCode : null
  );
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationMessage, setValidationMessage] = useState("");
  const debounceTimer = useRef(null);

  const handleReferralChoice = (choice) => {
    setHasReferralCode(choice);
    setFormData((prev) => ({
      ...prev,
      hasReferralCode: choice,
      agentReferralCode: choice ? prev.agentReferralCode : "",
      validatedReferralCode: false,
    }));
    
    if (!choice) {
      setValidationStatus(null);
      setValidationMessage("");
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    } else if (choice && formData.agentReferralCode?.trim()) {
      validateCode(formData.agentReferralCode);
    }
  };

  const validateCode = async (code) => {
    if (!code?.trim()) {
      setValidationStatus(null);
      setValidationMessage("");
      setFormData((prev) => ({
        ...prev,
        validatedReferralCode: false,
      }));
      if (onValidationStateChange) {
        onValidationStateChange({ isValidating: false, isValid: false });
      }
      return;
    }

    setIsValidating(true);
    setValidationStatus(null);
    setValidationMessage("");
    
    if (onValidationStateChange) {
      onValidationStateChange({ isValidating: true, isValid: false });
    }

    try {
      const response = await onValidateReferral(code);
      
      if (response.success) {
        setValidationStatus("success");
        setValidationMessage(response.message || "Referral code verified successfully!");
        setFormData((prev) => ({
          ...prev,
          validatedReferralCode: true,
          agentDetails: response.agentDetails || null,
        }));
        if (onValidationStateChange) {
          onValidationStateChange({ isValidating: false, isValid: true });
        }
      } else {
        setValidationStatus("error");
        setValidationMessage(response.message || "Invalid referral code. Please check and try again.");
        setFormData((prev) => ({
          ...prev,
          validatedReferralCode: false,
        }));
        if (onValidationStateChange) {
          onValidationStateChange({ isValidating: false, isValid: false });
        }
      }
    } catch (error) {
      setValidationStatus("error");
      setValidationMessage("Failed to validate code. Please try again.");
      setFormData((prev) => ({
        ...prev,
        validatedReferralCode: false,
      }));
      if (onValidationStateChange) {
        onValidationStateChange({ isValidating: false, isValid: false });
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleReferralCodeChange = (value) => {
    const upperValue = value.toUpperCase();
    setFormData((prev) => ({
      ...prev,
      agentReferralCode: upperValue,
      validatedReferralCode: false,
    }));

    // Reset validation status while typing
    setValidationStatus(null);
    setValidationMessage("");

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce validation - wait 800ms after user stops typing
    debounceTimer.current = setTimeout(() => {
      if (upperValue.trim().length >= 3) {
        validateCode(upperValue);
      } else if (upperValue.trim().length > 0) {
        // Code too short
        setValidationStatus("error");
        setValidationMessage("Referral code must be at least 3 characters");
        setFormData((prev) => ({
          ...prev,
          validatedReferralCode: false,
        }));
      }
    }, 800);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-6 md:p-8 sm:rounded-xl"
    >
      <div className="flex gap-2 items-start mb-4 sm:gap-3 md:gap-4 sm:mb-6">
        <div className="flex justify-center items-center w-12 h-12 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg sm:w-12 sm:h-12 shrink-0">
          <UserCheck size={24} className="sm:w-6 sm:h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold leading-tight text-gray-900 sm:text-xl md:text-2xl">Agent Referral Code</h2>
          <p className="mt-1.5 text-sm sm:text-sm text-gray-600 leading-relaxed">
            If you were referred by an agent, enter their referral code to receive special benefits
          </p>
        </div>
      </div>

      <div className="mb-5 sm:mb-6 md:mb-8">
        <label className="block mb-3 text-base font-semibold text-gray-900 sm:text-base">
          Do you have an agent referral code?
        </label>
        
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <motion.button
            type="button"
            onClick={() => handleReferralChoice(true)}
            whileTap={{ scale: 0.97 }}
            className={`relative min-h-[120px] p-5 sm:p-6 rounded-xl border-2 transition-all touch-manipulation ${
              hasReferralCode === true
                ? "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-500 shadow-md"
                : "bg-white border-gray-200 active:border-orange-300 active:shadow-sm"
            }`}
          >
            <div className="flex flex-col gap-2 justify-center items-center">
              <div className={`p-3 rounded-full ${
                hasReferralCode === true ? "bg-orange-500" : "bg-gray-100"
              }`}>
                <Tag size={24} className={hasReferralCode === true ? "text-white" : "text-gray-400"} />
              </div>
              <span className="text-base font-bold text-gray-900 sm:text-lg">Yes, I have a code</span>
              <span className="px-2 text-xs text-center text-gray-600 sm:text-sm">Enter your agent's referral code</span>
            </div>
            {hasReferralCode === true && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex absolute top-3 right-3 justify-center items-center w-6 h-6 text-white bg-orange-500 rounded-full"
              >
                <CheckCircle2 size={16} />
              </motion.div>
            )}
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleReferralChoice(false)}
            whileTap={{ scale: 0.97 }}
            className={`relative min-h-[120px] p-5 sm:p-6 rounded-xl border-2 transition-all touch-manipulation ${
              hasReferralCode === false
                ? "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-400 shadow-md"
                : "bg-white border-gray-200 active:border-gray-300 active:shadow-sm"
            }`}
          >
            <div className="flex flex-col gap-2 justify-center items-center">
              <div className={`p-3 rounded-full ${
                hasReferralCode === false ? "bg-gray-500" : "bg-gray-100"
              }`}>
                <Sparkles size={24} className={hasReferralCode === false ? "text-white" : "text-gray-400"} />
              </div>
              <span className="text-base font-bold text-gray-900 sm:text-lg">No, I don't have one</span>
              <span className="px-2 text-xs text-center text-gray-600 sm:text-sm">Continue without a referral code</span>
            </div>
            {hasReferralCode === false && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex absolute top-3 right-3 justify-center items-center w-6 h-6 text-white bg-gray-500 rounded-full"
              >
                <CheckCircle2 size={16} />
              </motion.div>
            )}
          </motion.button>
        </div>
      </div>

      {/* Referral Code Input (shown if user has a code) */}
      <AnimatePresence>
        {hasReferralCode === true && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-gradient-to-br from-orange-50 to-white rounded-xl border-2 border-orange-200 sm:p-5 md:p-6">
              <label className="flex gap-2 items-center mb-3 text-sm font-semibold text-gray-900 sm:text-sm">
                <Tag size={16} className="text-orange-600 shrink-0" />
                Enter Agent Referral Code <span className="text-red-500">*</span>
              </label>
              
              <div className="relative">
                <input
                  type="text"
                  value={formData.agentReferralCode || ""}
                  onChange={(e) => handleReferralCodeChange(e.target.value)}
                  className={`w-full px-4 py-3.5 pr-12 text-base font-mono font-bold tracking-wide text-gray-900 uppercase bg-white rounded-lg border-2 transition-all outline-none focus:ring-2 focus:ring-orange-500 touch-manipulation ${
                    isValidating
                      ? "border-orange-400 bg-orange-50"
                      : validationStatus === "success"
                      ? "border-green-500 bg-green-50"
                      : validationStatus === "error"
                      ? "border-red-500 bg-red-50"
                      : errors?.agentReferralCode
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="AGENT123"
                  maxLength={20}
                />
                
                <div className="flex absolute inset-y-0 right-0 items-center pr-3">
                  {isValidating && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <Loader2 size={20} className="text-orange-500 animate-spin" />
                    </motion.div>
                  )}
                  {!isValidating && validationStatus === "success" && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <CheckCircle2 size={20} className="text-green-500" />
                    </motion.div>
                  )}
                  {!isValidating && validationStatus === "error" && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <XCircle size={20} className="text-red-500" />
                    </motion.div>
                  )}
                </div>
              </div>

              <AnimatePresence>
                {validationMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`flex gap-2.5 items-start p-3.5 mt-3 rounded-lg ${
                      validationStatus === "success"
                        ? "bg-green-50 border border-green-200"
                        : "bg-red-50 border border-red-200"
                    }`}
                  >
                    {validationStatus === "success" ? (
                      <CheckCircle2 size={20} className="mt-0.5 text-green-600 shrink-0" />
                    ) : (
                      <AlertCircle size={20} className="mt-0.5 text-red-600 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium leading-relaxed ${
                        validationStatus === "success" ? "text-green-800" : "text-red-800"
                      }`}>
                        {validationMessage}
                      </p>
                      {validationStatus === "success" && formData.agentDetails && (
                        <p className="mt-1.5 text-xs text-green-700">
                          Agent: {formData.agentDetails.name || "Verified Agent"}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {errors?.agentReferralCode && !validationMessage && (
                <p className="flex gap-1.5 items-center mt-2.5 text-sm text-red-600">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{errors.agentReferralCode}</span>
                </p>
              )}

              <div className="flex gap-2.5 items-start p-3.5 mt-3 bg-blue-50 rounded-lg border border-blue-200">
                <AlertCircle size={18} className="mt-0.5 text-blue-600 shrink-0" />
                <p className="text-xs leading-relaxed text-blue-800 sm:text-sm">
                  <strong>Note:</strong> The referral code is case-insensitive. Validation happens automatically as you type.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {hasReferralCode === true && validationStatus === "success" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-3 items-center p-4 mt-5 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-md"
        >
          <div className="flex justify-center items-center w-12 h-12 bg-white rounded-full shrink-0">
            <Sparkles size={22} className="text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight text-white sm:text-base">Great! You're eligible for special benefits</p>
            <p className="mt-1 text-xs text-green-100 sm:text-sm">Your agent will be notified about your registration</p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

